import { Flex, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { Bundle } from "../../../types";
import { FunnelComponentProps } from "../../funnel/types";
import { buildBundleCheckoutPayload } from "../../slideInMenu/bundleDetails/hooks/useBundlePurchase";
import { PurchaseButton } from "./PurchaseButton";
import { useUserData } from "../../../hooks/useUserData";
import { useIsShowingEmbeddedStripeForm } from "../hooks/useIsShowingEmbeddedStripeForm";
import { EmbeddedStripePaymentForm } from "./EmbeddedStripePaymentForm";
import {
  BundlePaymentSetupPayload,
  createBundlePurchaseFromConfirmationToken,
  createBundlePurchaseFromSavedPaymentMethod,
  isBundlePaymentSetupPayload,
} from "../api/paymentApi";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { patchAllQueriesWithCompletedEntity } from "../utils/replaceCompletedEntityInCache";
import { isMatchingBundleEntity } from "../utils/cacheMatchers";
import { createSessionPayloadStore } from "../utils/sessionPayloadStore";
import {
  saveGuestPendingPurchase,
  clearGuestPendingPurchase,
} from "../utils/guestPendingPurchase";
import { usePaymentCompletion } from "../hooks/usePaymentCompletion";
import { usePendingAuthPurchaseResume } from "../hooks/usePendingAuthPurchaseResume";

export interface PurchaseBundleModalFooterProps extends FunnelComponentProps {
  bundle?: Bundle;
}

const bundlePayloadStore = createSessionPayloadStore<BundlePaymentSetupPayload>(
  "pendingBundlePaymentSetup",
  isBundlePaymentSetupPayload,
);

export function PurchaseBundleModalFooter({
  bundle,
  onClose,
  onSuccess: onSuccessProp,
  setFunnelType,
}: PurchaseBundleModalFooterProps) {
  if (!bundle) return null;
  const { userData } = useUserData();
  const queryClient = useQueryClient();
  const showEmbeddedForm = useIsShowingEmbeddedStripeForm();

  const isGoldPricePoint = !!userData?.gold;
  const bundleCheckoutPayload = bundle.user_owns_all_items
    ? undefined
    : buildBundleCheckoutPayload(bundle, isGoldPricePoint);

  type BundlePurchaseResponse = Awaited<
    ReturnType<
      | typeof createBundlePurchaseFromConfirmationToken
      | typeof createBundlePurchaseFromSavedPaymentMethod
    >
  >;

  const updateCompletedBundleCache = async (
    response: BundlePurchaseResponse,
  ) => {
    const completedBundle = response.data.payload.results;

    // Collect every bundleDetail variant pointing at this bundle. We need this
    // explicit pass because cached bundleDetail responses don't always carry
    // `entity_type`, so the deep-patch matcher below would miss them.
    const directDetailKeys = new Map<string, readonly unknown[]>();
    const enqueueDirectKey = (queryKey: readonly unknown[]) => {
      directDetailKeys.set(JSON.stringify(queryKey), queryKey);
    };
    enqueueDirectKey(["bundleDetail", bundle.uuid]);
    enqueueDirectKey(["bundleDetail", completedBundle.uuid]);
    enqueueDirectKey(["bundleDetail", String(completedBundle.id)]);
    queryClient
      .getQueriesData<BundlePurchaseResponse>({ queryKey: ["bundleDetail"] })
      .forEach(([queryKey, cachedResponse]) => {
        const cachedBundle = cachedResponse?.data?.payload?.results;
        if (
          cachedBundle?.uuid === completedBundle.uuid ||
          cachedBundle?.id === completedBundle.id
        ) {
          enqueueDirectKey(queryKey);
        }
      });

    directDetailKeys.forEach((queryKey) => {
      queryClient.setQueryData(queryKey, response);
    });

    // Embedded references inside other queries (browse pages, comic details, etc.)
    // get caught by the deep matcher.
    patchAllQueriesWithCompletedEntity(
      queryClient,
      completedBundle,
      isMatchingBundleEntity,
    );

    await Promise.all(
      Array.from(directDetailKeys.values()).map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  };

  const buildBundlePaymentSetupPayload = (
    confirmationTokenId: string,
  ): BundlePaymentSetupPayload => ({
    ...(bundleCheckoutPayload ??
      buildBundleCheckoutPayload(bundle, isGoldPricePoint)),
    checkoutFlow: "bundle_payment_setup",
    bundleUuid: bundle.uuid,
    confirmationTokenId,
  });

  const finishSuccess = () => {
    if (onSuccessProp) {
      onSuccessProp({});
      return;
    }
    onClose?.();
  };

  const {
    run: runBundlePurchase,
    runAndSwallow: runBundlePurchaseAndSwallow,
    isLoading: isCompletingPurchase,
    error: checkoutError,
  } = usePaymentCompletion<BundlePurchaseResponse>({
    updateCache: updateCompletedBundleCache,
    onSuccess: () => {
      clearGuestPendingPurchase(bundlePayloadStore);
      finishSuccess();
    },
    fallbackMessage: "An error occurred during purchase",
  });

  usePendingAuthPurchaseResume<BundlePaymentSetupPayload>({
    enabled: !!userData,
    entityKey: bundle.uuid,
    payloadStore: bundlePayloadStore,
    payloadMatchesEntity: (payload) => payload.bundleUuid === bundle.uuid,
    onResume: (payload) =>
      runBundlePurchase(() =>
        createBundlePurchaseFromConfirmationToken(bundle.uuid, payload),
      ),
  });

  const handleSavedPaymentMethodPurchase = () => {
    if (!bundleCheckoutPayload) return;
    runBundlePurchaseAndSwallow(() =>
      createBundlePurchaseFromSavedPaymentMethod(
        bundle.uuid,
        bundleCheckoutPayload,
      ),
    );
  };

  const handlePaymentSetupComplete = (confirmationTokenId: string) => {
    const payload = buildBundlePaymentSetupPayload(confirmationTokenId);

    if (!userData) {
      saveGuestPendingPurchase(payload, bundlePayloadStore);
      setFunnelType(FUNNEL_TYPES.signup);
      return;
    }

    runBundlePurchaseAndSwallow(() =>
      createBundlePurchaseFromConfirmationToken(bundle.uuid, payload),
    );
  };

  return (
    <Flex flexDirection="column" width="100%" gap="24px">
      {bundle.user_owns_all_items ? (
        <PurchaseButton text="View Bundle" onClick={finishSuccess} />
      ) : (
        <Flex flexDirection="column" gap="8px" width="100%">
          {isCompletingPurchase ? (
            <PurchaseButton
              text="Confirming your purchase…"
              loading
              isDisabled
            />
          ) : showEmbeddedForm ? (
            <EmbeddedStripePaymentForm
              submitButtonText={t("components.payments.bundles.buyBundleNow")}
              disabled={isCompletingPurchase}
              onComplete={handlePaymentSetupComplete}
            />
          ) : (
            <PurchaseButton
              text={`${t("components.payments.bundles.buyBundleNow")}`}
              onClick={handleSavedPaymentMethodPurchase}
              loading={isCompletingPurchase}
              isDisabled={isCompletingPurchase}
            />
          )}
          {checkoutError ? (
            <Text color="error.300" fontSize="14px" lineHeight="130%">
              {checkoutError}
            </Text>
          ) : null}
        </Flex>
      )}
    </Flex>
  );
}
